import React, {createRef} from 'react'
import Plotly from 'plotly.js';
import createPlotlyComponent from 'react-plotly.js/factory';
import _ from 'lodash';

const Plot = createPlotlyComponent(Plotly);

interface GraphProps {
    configDate?: String;
    index?: number;
    gridsCount?: number;
    x?: any;
    y?: any;
    threshold?: any;
    param?: any;
    sensor_values?: any;
    sensorBox?: any;
}

class E2Graph extends React.PureComponent<GraphProps, {}> {

    graphContainerRef: React.RefObject<HTMLDivElement | null> = createRef()
    layouts = (sensors: any) => {
        let result: any = {
            width: this.graphContainerRef?.current?.offsetWidth,
            legend: {
                x: 0,
                y: 100
            },
            autosize: true,
            xaxis: {
                type: "date",
                autorange: true,
                tickformat: '%I:%M:%S %p\n%B %d, %Y',
                uirevision: 'time',
            }
        };

        // Get selected pin types from sensors
        const pinTypes = new Set();
        sensors?.forEach((sensor: any) => {
            if (sensor.pin_type) {
                pinTypes.add(sensor.pin_type);
            }
        });
        
        const selectedTypes = Array.from(pinTypes);

        if (selectedTypes.length === 0) {
            return result;
        }

        // Configure y-axes based on selected pin types
        if (selectedTypes.length === 1) {
            const pinType = selectedTypes[0];
            result.yaxis = {
                type: 'linear',
                title: pinType,
                titlefont: { color: 'black' },
                tickfont: { color: 'black' },
                linewidth: 1,
                side: 'left'
            };
            result.legend.x = 0;
            result.legend.y = 1.3;
            result.legend.xanchor = 'left';
            result.legend.yanchor = 'top';
            result.legend.orientation = 'h';
            result.legend.traceorder = 'normal';
        } else if (selectedTypes.length === 2) {
            const [leftType, rightType] = selectedTypes;
            
            result.yaxis = {
                type: 'linear',
                title: leftType,
                titlefont: { color: 'black' },
                tickfont: { color: 'black' },
                linewidth: 1,
                side: 'left'
            };
            
            result.yaxis2 = {
                type: 'linear',
                title: rightType,
                titlefont: { color: 'black' },
                tickfont: { color: 'black' },
                overlaying: 'y',
                side: 'right',
                linewidth: 1
            };

            // Create annotations and shapes for both left and right legends with multiple rows support
            const leftAxisSensors = sensors?.filter((sensor: any) => sensor.yaxis === 'y' || sensor.yaxis === 'y1') || [];
            const rightAxisSensors = sensors?.filter((sensor: any) => sensor.yaxis === 'y2') || [];
            const plotlyDefaultColors = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'];
            
            result.annotations = [];
            result.shapes = [];
            
            // Configuration for multi-row layout
            const itemWidth = this.props.gridsCount == 1 ? 0.25 : 0.50;
            const maxWidth = 0.5; // Maximum 50% of viewport for each side
            const itemsPerRow = Math.floor(maxWidth / itemWidth);
            const rowHeight = 0.065; // Vertical spacing between rows
            const startY = this.props.gridsCount == 1 ? 1.25 : 1.45;
            
            // Helper function to split array into chunks
            const chunkArray = (arr: any[], size: number) => {
                const chunks = [];
                for (let i = 0; i < arr.length; i += size) {
                    chunks.push(arr.slice(i, i + size));
                }
                return chunks;
            };
            
            // Left legend: position in left 50% of viewport (0 to 0.5) with multiple rows
            const leftStartX = 0;
            const leftRows = chunkArray(leftAxisSensors, itemsPerRow);
            const maxRows = Math.max(leftRows.length, chunkArray(rightAxisSensors, itemsPerRow).length);
            
            leftRows.forEach((row: any[], rowIndex: number) => {
                row.forEach((sensor: any, itemIndex: number) => {
                    const color = sensor.line?.color || sensor.marker?.color || plotlyDefaultColors[(rowIndex * itemsPerRow + itemIndex) % plotlyDefaultColors.length];
                    const xPos = leftStartX + (itemIndex * itemWidth);
                    const yPos = startY - (rowIndex * rowHeight);
                    
                    // Ensure we don't exceed the left half boundary
                    if (xPos + itemWidth > maxWidth) {
                        return;
                    }
                    
                    // Add shape for line indicator
                    result.shapes.push({
                        type: 'line',
                        xref: 'paper',
                        yref: 'paper',
                        x0: this.props.gridsCount == 1 ? xPos : -0.5,
                        y0: yPos,
                        x1: this.props.gridsCount == 1 ? xPos + 0.03 : -0.15,
                        y1: yPos,
                        line: {
                            color: color,
                            width: 2
                        }
                    });
                    
                    // Add annotation for text
                    result.annotations.push({
                        x: this.props.gridsCount == 1 ? xPos + 0.035 : -0.15 + 0.035,
                        y: yPos + 0.035,
                        xref: 'paper',
                        yref: 'paper',
                        xanchor: 'left',
                        yanchor: 'top',
                        text: sensor.name,
                        showarrow: false,
                        font: { size: 11, family: '"Open Sans", verdana, arial, sans-serif' },
                        bgcolor: 'rgba(255, 255, 255, 0.8)'
                    });
                });
            });
            
            // Right legend: position in right 50% of viewport (0.5 to 1.0) with multiple rows
            const rightHalfStart = 0.5;
            const rightRows = chunkArray(rightAxisSensors, itemsPerRow);
            
            rightRows.forEach((row: any[], rowIndex: number) => {
                const totalRowWidth = row.length * itemWidth;
                // Start from right half, but if items are too many, start from right edge
                const rightStartX = totalRowWidth <= maxWidth ? rightHalfStart : (1 - totalRowWidth);
                
                row.forEach((sensor: any, itemIndex: number) => {
                    const colorIndex = leftAxisSensors.length + (rowIndex * itemsPerRow + itemIndex);
                    const color = sensor.line?.color || sensor.marker?.color || plotlyDefaultColors[colorIndex % plotlyDefaultColors.length];
                    const xPos = rightStartX + (itemIndex * itemWidth);
                    const yPos = startY - (rowIndex * rowHeight);
                    
                    // Ensure we don't exceed the right half boundary
                    if (xPos < rightHalfStart) {
                        return;
                    }
                    
                    // Add shape for line indicator
                    result.shapes.push({
                        type: 'line',
                        xref: 'paper',
                        yref: 'paper',
                        x0: xPos,
                        y0: yPos,
                        x1: this.props.gridsCount == 1 ? xPos + 0.03 : xPos + 0.05,
                        y1: yPos,
                        line: {
                            color: color,
                            width: 2
                        }
                    });
                    
                    // Add annotation for text
                    result.annotations.push({
                        x: this.props.gridsCount == 1 ? xPos + 0.035 : xPos + 0.055,
                        y: yPos + 0.035,
                        xref: 'paper',
                        yref: 'paper',
                        xanchor: 'left',
                        yanchor: 'top',
                        text: sensor.name,
                        showarrow: false,
                        font: { size: 11, family: '"Open Sans", verdana, arial, sans-serif' },
                        bgcolor: 'rgba(255, 255, 255, 0.8)'
                    });
                });
            });
            
            // Adjust top margin based on number of rows
            if (!result.margin) {
                result.margin = {};
            }
            if (this.props.gridsCount == 1) {
                result.margin.t = 120;
            } else {
                result.margin.t = 155;
            }
        }

        // Handle switch sensors
        sensors?.forEach((sensor: any, index: number) => {
            if (sensor.sensor_type === 'switch') {
                const axisName = index === 0 ? 'yaxis' : 'yaxis2';
                if (result[axisName]) {
                    result[axisName].tickvals = [0, 1];
                }
            }
        });

        return result;
    }

    addhighlightToThresholds = (min:any,max:any,label:string,xValues:any)=>{
            const minLineTrace=[{
                x:xValues,
                y:Array(xValues.length).fill(min),
                mode:'lines',
                line:{
                    color:'orange',
                    width:2
                },
                yaxis:'y1',
                name:`${label} Minimum`
            }]
        const maxLineTrace=[{
            x:xValues,
            y:Array(xValues.length).fill(max),
            mode:'lines',
            line:{
                color:'red',
                width:2
            },
            yaxis:'y1',
            name:`${label} Maximum`
        }]
            return [...minLineTrace,...maxLineTrace]
    }

    handleCopy = async () => {
      try {
        const plotElement = document.querySelector('.js-plotly-plot');
        if (!plotElement) {
            alert('Plot not found!');
            return;
        }

        // Use Plotly's built-in toImage method
        const imageData = await Plotly.toImage(plotElement as any, {
            format: 'png',
            height: 400,
            width: 1200,
            scale: 3
        });

        // Convert base64 to Blob
        const response = await fetch(imageData);
        const blob = await response.blob();

        // Copy image to clipboard
        await navigator.clipboard.write([
            new ClipboardItem({
                'image/png': blob,
            }),
        ]);
      } catch (error) {
        console.error('Clipboard copy failed:', error);
        alert('Copy failed');
      }
    };

      customCopyIcon = {
        width: 524,
        height: 524,
        path: "M408 112H184c-13.2 0-24 10.8-24 24v32h48v-16h176v176h-16v48h32c13.2 0 24-10.8 24-24V136c0-13.2-10.8-24-24-24zM328 192H104c-13.2 0-24 10.8-24 24v208c0 13.2 10.8 24 24 24h224c13.2 0 24-10.8 24-24V216c0-13.2-10.8-24-24-24zM320 416H112V224h208v192z"
      };

      customMailIcon = {
        width: 512,
        height: 512,
        path: "M467 61H45C20.2 61 0 81.2 0 106v300c0 24.8 20.2 45 45 45h422c24.8 0 45-20.2 45-45V106c0-24.8-20.2-45-45-45zM256 295L45 151v-5h422v5L256 295z"
      };
      
    render() {
        const { index, configDate, param, x, y, threshold, sensor_values } = this.props
        let datas = param?.map((data: any) => {
            const sensorData = sensor_values?.find((sensor: any) => (sensor?.pin_with_representation === data?.value));
            if (!sensorData) return null;
            let result = {
                x: x[data.value],
                y: y[data.value],
                mode: 'lines+markers',
                type: 'scattergl',
                name: sensorData.actual_name || sensorData.display_name || sensorData.prop,
                representation: sensorData.representation,
                pin_type: sensorData.type || 'default',
                pin_with_representation: sensorData.pin_with_representation
            } as any;
            
            return result;
        }).filter(Boolean);
        
        // Group sensors by pin_type and assign to appropriate axis
        const pinTypeGroups: any = {};
        datas.forEach((sensor: any) => {
            const pinType = sensor.pin_type;
            if (!pinTypeGroups[pinType]) {
                pinTypeGroups[pinType] = [];
            }
            pinTypeGroups[pinType].push(sensor);
        });
        
        const pinTypes = Object.keys(pinTypeGroups);
        
        // Assign y-axis based on pin_type
        if (pinTypes.length === 2) {
            const [firstType, secondType] = pinTypes;
            pinTypeGroups[firstType].forEach((sensor: any) => {
                sensor.yaxis = 'y';
                sensor.legendgroup = 'left';
                sensor.showlegend = false; // Using annotations instead
            });
            pinTypeGroups[secondType].forEach((sensor: any) => {
                sensor.yaxis = 'y2';
                sensor.legendgroup = 'right';
                sensor.showlegend = false; // Using annotations instead
            });
            
            // Reorder datas so left-axis sensors come first, then right-axis sensors
            const leftAxisSensors = datas.filter((sensor: any) => sensor.yaxis === 'y' || sensor.yaxis === 'y1');
            const rightAxisSensors = datas.filter((sensor: any) => sensor.yaxis === 'y2');
            datas = [...leftAxisSensors, ...rightAxisSensors];
        } else if (pinTypes.length === 1) {
            // Single pin type, all sensors on left axis
            pinTypeGroups[pinTypes[0]].forEach((sensor: any) => {
                sensor.yaxis = 'y';
                sensor.legendgroup = 'left';
                sensor.showlegend = true;
            });
        }
        
        // Add threshold lines for single sensor selections
        if (datas.length === 1 && threshold?.hasOwnProperty(datas[0].pin_with_representation)) {
            const { min, max } = threshold[datas[0].pin_with_representation];
            const thresholdTraces = this.addhighlightToThresholds(min, max, datas[0].name, datas[0].x);
            // Assign the same yaxis as the sensor
            thresholdTraces.forEach((trace: any) => {
                trace.yaxis = datas[0].yaxis || 'y';
            });
            datas = [...datas, ...thresholdTraces];
        }

        return (
            <div
                className="graph-container"
                key={index}
                hidden={
                    !!(y && Object.keys(y).length === 0)
            }
                ref={this.graphContainerRef}
            >

                <div id="plot-container">
                <Plot
                    data={datas}
                    layout={this.layouts(datas)}
                    config={{
                        scrollZoom: false,
                        displaylogo: false,
                        toImageButtonOptions: {
                            format: 'svg', // one of png, svg, jpeg, webp
                            filename: 'custom_image',
                            height: 500,
                            width: 700,
                            scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
                        },
                        modeBarButtonsToAdd: [{
                            title: 'Copy plot',
                            name: 'Copy plot',
                            icon: this.customCopyIcon,
                            click: this.handleCopy,
                          }],
                        responsive: true,
                        displayModeBar: true,
                    }}
                />
                </div>
                <p className='font-w-600'>{configDate ? configDate : ''}</p>
            </div>
        );
    }
}

export default E2Graph